import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Award, Users, Wrench } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import { SITE } from "@/lib/siteData";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="Who We Are"
        title="A land management company you can trust."
        subtitle="Mulberry Land Services treats every property like it's our own — combining honest communication, dependable service, and quality workmanship across residential, commercial, and agricultural projects."
        image="https://images.unsplash.com/photo-1763515186653-2df0fba44697"
      />

      {/* STORY */}
      <section className="bg-[#F9F8F6] py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-6 md:px-12">
          <div className="space-y-6 text-base leading-relaxed text-[#5C5C5C] md:text-lg">
            <p>
              At Mulberry Land Services, we believe every project deserves the
              highest level of care, professionalism, and attention to detail.
              As a family-owned and operated business, we take pride in treating
              every property as if it were our own. Our reputation is built on
              honest communication, dependable service, and quality workmanship
              that stands the test of time.
            </p>
            <p>
              We specialize in land clearing, forestry mulching, brush removal,
              site preparation, grading, and property improvement for
              residential, commercial, and agricultural clients. Whether you're
              preparing land for a new home, reclaiming overgrown property, or
              improving your land's usability, our team is committed to
              delivering efficient, reliable results.
            </p>
            <p>
              We understand that every project is unique, which is why we work
              closely with each customer to provide tailored solutions that meet
              their goals, timeline, and budget. From the first phone call to
              the final walkthrough, our focus is on doing the job right — not
              just getting it done.
            </p>
            <p>
              At Mulberry Land Services, quality isn't just a promise — it's
              the standard we bring to every project. We value hard work,
              integrity, and the relationships we build with our customers. When
              you choose us, you're choosing a family-run company that's
              dedicated to earning your trust through exceptional service and
              lasting results.
            </p>
          </div>
        </div>
      </section>

      {/* VALUES GRID */}
      <section className="bg-[#E8E2DA] py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <p className="eyebrow">What we stand for</p>
          <h2 className="mt-3 max-w-3xl font-display text-3xl font-black tracking-tight text-[#16302B] sm:text-4xl lg:text-5xl">
            Four things we'll never compromise on.
          </h2>

          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { Icon: ShieldCheck, t: "Licensed & Insured", d: "Liability and workers' comp on every job, every operator." },
              { Icon: Award, t: "Dependable Service", d: "On-time, follow-through, and clear communication from quote to final walkthrough." },
              { Icon: Users, t: "Same Crew", d: "Owner-operated. You'll work with the same people start to finish." },
              { Icon: Wrench, t: "Right Equipment", d: "Mulchers, dozers, excavators — we pair the machine to the job." },
            ].map(({ Icon, t, d }) => (
              <div key={t} className="border border-[#16302B]/15 bg-white p-7">
                <span className="grid h-11 w-11 place-items-center bg-[#16302B] text-[#D95D39]">
                  <Icon size={20} strokeWidth={2} />
                </span>
                <h3 className="mt-5 font-display text-lg font-bold tracking-tight text-[#16302B]">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#5C5C5C]">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#F9F8F6] py-20 md:py-28">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-10 px-6 md:flex-row md:items-end md:px-12">
          <h2 className="max-w-2xl font-display text-3xl font-black tracking-tight text-[#16302B] sm:text-4xl">
            Got a property in mind? Let's walk it together.
          </h2>
          <Link
            to="/contact"
            data-testid="about-cta"
            className="inline-flex items-center gap-2 bg-[#D95D39] px-7 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#B94B2C]"
          >
            Request a Quote <ArrowRight size={16} strokeWidth={2.5} />
          </Link>
        </div>
      </section>
    </div>
  );
}
